import { Component, OnInit, ChangeDetectionStrategy, ElementRef, NgZone, OnDestroy, ViewChild } from '@angular/core';
import { formatDate } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { forkJoin } from 'rxjs';
import type { editor } from 'monaco-editor/editor';

import { AlertService, UIService } from 'src/app/_services';
import {
  formatDurationMs,
  MAX_IMPORT_FILE_BYTES,
  MEDIA_IMPORT_STATUS_LABELS,
  MediaImport,
  MediaImportService,
} from 'src/app/_services/media-import.service';
import {
  jsonProblems,
  JsonProblem,
  locateParseError,
  positionAt,
  sessionOffsets,
  summarizeTracsMedia,
  TracsMediaSummary,
  TracsSessionSummary,
} from 'src/app/_utils/tracs-summary';
import { formatBytes } from 'src/app/_utils/utils';

/** Up to this size the JSON is shown formatted; Format pretty-prints a larger one on request */
const FORMAT_LIMIT = 5 * 1024 * 1024;

/**
 * The export (TRACS JSON) an import read, as invenire-v2's media JSON page: the file in a read-only
 * editor, why the import failed or where the JSON breaks, the import's details, and a summary of its
 * media and backup sessions. A media can come from many exports: the explorer lists them, each opens here.
 */
@Component({
    selector: 'app-import-json',
    templateUrl: './import-json.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ImportJsonComponent implements OnInit, OnDestroy {
  projectId: string;
  importId: string;
  record: MediaImport;
  loading = true;
  msg: string;

  /** the file as stored, and what the editor shows of it */
  private blob: Blob;
  text: string;
  shown: string;
  formatted = false;
  tooLarge = false;
  parseError: string;
  parseErrorAt: { line: number, column: number };
  summary: TracsMediaSummary;
  problems: JsonProblem[] = [];

  /** the editor, once its module has loaded */
  viewer: editor.IStandaloneCodeEditor;
  wrap = true;
  private destroyed = false;

  readonly statusLabel = MEDIA_IMPORT_STATUS_LABELS;
  readonly formatDurationMs = formatDurationMs;
  readonly maxBytes = MAX_IMPORT_FILE_BYTES;

  constructor(
    private route: ActivatedRoute,
    private mediaImportService: MediaImportService,
    private alert: AlertService,
    private uiservice: UIService,
    private zone: NgZone,
  ) { }

  /** set once the page shows the editor's box */
  @ViewChild('editor') set editorHost(host: ElementRef<HTMLElement>) {
    if (host) { this.mount(host.nativeElement); }
  }

  ngOnInit() {
    this.uiservice.setTitle('Import JSON');
    this.projectId = this.route.snapshot.paramMap.get('id');
    this.importId = this.route.snapshot.paramMap.get('importId');
    forkJoin([
      this.mediaImportService.get(this.projectId, this.importId),
      this.mediaImportService.source(this.projectId, this.importId),
    ]).subscribe({
      next: async ([{ import: record }, blob]) => {
        this.record = record;
        this.blob = blob;
        this.show(await blob.text());
        this.loading = false;
      },
      error: ({ status }) => {
        this.loading = false;
        this.msg = status === 404
          ? 'The uploaded JSON for this import is no longer available'
          : 'Something went wrong';
      },
    });
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.viewer?.dispose();
  }

  private show(raw: string) {
    // a byte-order mark would show as a stray character on line 1
    this.text = raw.charCodeAt(0) === 0xfeff ? raw.slice(1) : raw;
    let parsed: unknown;
    try {
      parsed = JSON.parse(this.text);
    } catch(err) {
      this.parseError = err instanceof Error ? err.message : 'Failed to parse JSON';
      this.parseErrorAt = locateParseError(this.text, this.parseError);
    }
    this.summary = this.parseError ? null : summarizeTracsMedia(parsed);
    this.problems = jsonProblems(this.parseError, this.parseErrorAt, []);
    // a larger file than an upload may be is offered as a download only
    this.tooLarge = this.blob.size > MAX_IMPORT_FILE_BYTES;
    // formatted unless it is invalid (positions in the error must hold) or large
    this.formatted = !this.parseError && this.text.length <= FORMAT_LIMIT;
    this.shown = this.formatted ? JSON.stringify(parsed, null, 2) : this.text;
  }

  /** load the editor's module (a chunk of its own) and open the JSON in it */
  private async mount(host: HTMLElement) {
    const { createJsonViewer } = await import('./json-viewer');
    const error = this.parseError ? { message: this.parseError, ...this.parseErrorAt } : null;
    // outside Angular, so the editor's own events don't run change detection
    const viewer = await this.zone.runOutsideAngular(() => createJsonViewer(host, this.shown, error, markers =>
      this.zone.run(() => this.problems = jsonProblems(this.parseError, this.parseErrorAt, markers))));
    if (this.destroyed) { viewer.dispose(); } else { this.viewer = viewer; }
  }

  get failed() {
    return this.record?.status === 'failed';
  }

  get running() {
    return this.record.status === 'queued' || this.record.status === 'processing';
  }

  /** invenire-v2's line for a backup session: date · copy type · files · size */
  sessionDetails(session: TracsSessionSummary) {
    return [
      session.backupDate !== null && formatDate(session.backupDate * 1000, 'MM/dd/yyyy', 'en-US'),
      session.copyType,
      `${session.fileCount.toLocaleString('en-US')} ${session.fileCount === 1 ? 'file' : 'files'}`,
      formatBytes(session.totalSize),
    ].filter(Boolean).join(' · ');
  }

  /** put the cursor at a line and column (unfolding what hides it) and scroll there */
  reveal({ line, column }: { line: number, column: number }) {
    const position = { lineNumber: line, column };
    this.viewer?.setPosition(position);
    this.viewer?.revealPositionInCenter(position);
    this.viewer?.focus();
  }

  revealSession(index: number) {
    const offset = sessionOffsets(this.shown)[index];
    if (offset !== undefined) { this.reveal(positionAt(this.shown, offset)); }
  }

  /** an editor action: editor.foldAll, actions.find, editor.action.gotoLine... */
  run(action: string) {
    this.viewer?.getAction(action)?.run();
  }

  toggleWrap() {
    this.wrap = !this.wrap;
    this.viewer?.updateOptions({ wordWrap: this.wrap ? 'on' : 'off' });
  }

  format() {
    this.shown = JSON.stringify(JSON.parse(this.shown), null, 2);
    this.formatted = true;
    this.viewer?.setValue(this.shown);
  }

  download() {
    const url = URL.createObjectURL(this.blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = this.record.fileName;
    link.click();
    URL.revokeObjectURL(url);
  }

  copy() {
    return this.copyText(this.text, 'JSON copied to clipboard', 'Could not copy the JSON');
  }

  copyJobId() {
    return this.copyText(this.record.jobId, 'Job ID copied to clipboard', 'Could not copy the job ID');
  }

  private async copyText(text: string, copied: string, failed: string) {
    try {
      await navigator.clipboard.writeText(text);
      this.alert.success(copied);
    } catch {
      this.alert.error(failed);
    }
  }

  back() {
    history.back();
  }
}
